import { Sensor, SensorEvent, SensorType, useSensorEvents } from 'common';
import { useMemo } from 'react';

import { useAppSync } from '../../contexts/AppSyncContext';

export const useLatestSensorEvents = (
  sensorType: SensorType,
  indoor?: boolean,
): {
  sensor: Sensor | undefined;
  event: SensorEvent | undefined;
  events: SensorEvent[];
  inProgress: boolean;
} => {
  const { sensors, selectedHubId, sensorsLoading } = useAppSync();

  const matchedSensor = useMemo(() => {
    // Helper to determine if a sensor matches the indoor flag
    const matchesIndoor = (sensor: Sensor) => {
      if (indoor === undefined) return true;
      // If sensorGroupConfiguration is undefined, treat as indoor = true
      if (!sensor.sensorGroupConfiguration) return indoor === true;
      return sensor.sensorGroupConfiguration.indoor === indoor;
    };

    return sensors.find(
      (sensor) =>
        sensor.hubId === selectedHubId && sensor.type === sensorType && matchesIndoor(sensor),
    );
  }, [sensors, selectedHubId, sensorType, indoor]);

  const { sensorEvents, loading: sensorEventsLoading } = useSensorEvents(
    selectedHubId ?? undefined,
    matchedSensor?.id,
  );

  const filteredSensorEvents = useMemo(
    () => sensorEvents.filter((sensorEvent) => sensorEvent.eventType === 'DATA'),
    [sensorEvents],
  );

  const latestEvent = useMemo(
    () =>
      filteredSensorEvents.find(
        (sensorEvent) => sensorEvent.parsedMetadata?.measurementType === sensorType,
      ),
    [filteredSensorEvents, sensorType],
  );

  const inProgress = useMemo(
    () => sensorsLoading || sensorEventsLoading,
    [sensorsLoading, sensorEventsLoading],
  );

  return {
    sensor: matchedSensor,
    event: matchedSensor ? latestEvent : undefined,
    events: matchedSensor ? filteredSensorEvents : [],
    inProgress,
  };
};
